import type { PdType } from './measurements';
import type { EyePrescription, PrismBase } from './sales';

/**
 * Eye examinations and the prescriptions they issue.
 *
 * The clinical source of truth. An order's SalesOrderPrescription is a copy made from a completed prescription, and a customer's PD history stays in CustomerMeasurement: a prescription links one measurement and freezes its figures when it is issued.
 */

/** Only a draft is edited. A completed examination is corrected by an amendment whose completion voids it. */
export const EyeExaminationStatus = {
  DRAFT: 'DRAFT',
  COMPLETED: 'COMPLETED',
  VOIDED: 'VOIDED',
} as const;
export type EyeExaminationStatus = (typeof EyeExaminationStatus)[keyof typeof EyeExaminationStatus];

/** What a screen shows. REPLACED and DISCARDED are voided examinations, told apart by how they ended; they are never stored. */
export const EyeExaminationDisplayStatus = {
  DRAFT: 'DRAFT',
  COMPLETED: 'COMPLETED',
  VOIDED: 'VOIDED',
  /** Voided by the completion of its amendment. */
  REPLACED: 'REPLACED',
  /** A draft voided before it was ever completed. */
  DISCARDED: 'DISCARDED',
} as const;
export type EyeExaminationDisplayStatus =
  (typeof EyeExaminationDisplayStatus)[keyof typeof EyeExaminationDisplayStatus];

export const AcuityDistance = { DISTANCE: 'DISTANCE', NEAR: 'NEAR' } as const;
export type AcuityDistance = (typeof AcuityDistance)[keyof typeof AcuityDistance];

/** What the eye looked through when acuity was tested. */
export const AcuityCorrection = {
  UNAIDED: 'UNAIDED',
  /** The customer's current glasses or lenses. */
  HABITUAL: 'HABITUAL',
  /** The refraction found in this examination. */
  BEST_CORRECTED: 'BEST_CORRECTED',
} as const;
export type AcuityCorrection = (typeof AcuityCorrection)[keyof typeof AcuityCorrection];

/** How distance acuity is written. Values are kept as entered and never converted. */
export const DistanceAcuityNotation = {
  SNELLEN_METRIC: 'SNELLEN_METRIC',
  DECIMAL: 'DECIMAL',
} as const;
export type DistanceAcuityNotation =
  (typeof DistanceAcuityNotation)[keyof typeof DistanceAcuityNotation];

/** How near acuity is written. Values are kept as entered and never converted. */
export const NearAcuityNotation = { N_POINT: 'N_POINT', DECIMAL: 'DECIMAL' } as const;
export type NearAcuityNotation = (typeof NearAcuityNotation)[keyof typeof NearAcuityNotation];

/** Written in any notation when no chart line can be read: count fingers, hand motion, light perception, no light perception. */
export const ACUITY_CATEGORIES = ['CF', 'HM', 'LP', 'NLP'] as const;
export type AcuityCategory = (typeof ACUITY_CATEGORIES)[number];

export const RefractionKind = {
  /** The correction the customer arrived with. */
  HABITUAL: 'HABITUAL',
  /** Autorefraction or retinoscopy. */
  OBJECTIVE: 'OBJECTIVE',
  /** The examiner's subjective refraction. */
  SUBJECTIVE: 'SUBJECTIVE',
} as const;
export type RefractionKind = (typeof RefractionKind)[keyof typeof RefractionKind];

/** Derived for display; nothing is blocked by it. Expiry is only ever what the examiner entered. */
export const ClinicalPrescriptionStatus = {
  /** On a draft examination: not issued yet. */
  DRAFT: 'DRAFT',
  VALID: 'VALID',
  EXPIRED: 'EXPIRED',
  VOIDED: 'VOIDED',
  REPLACED: 'REPLACED',
} as const;
export type ClinicalPrescriptionStatus =
  (typeof ClinicalPrescriptionStatus)[keyof typeof ClinicalPrescriptionStatus];

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

export interface VisualAcuityEntry {
  distance: AcuityDistance;
  correction: AcuityCorrection;
  /** OD, as written. */
  right: string | null;
  /** OS, as written. */
  left: string | null;
  /** OU, as written. */
  binocular: string | null;
}

export interface RefractionEntry {
  kind: RefractionKind;
  right: EyePrescription;
  left: EyePrescription;
}

export interface ClinicalPrescriptionPd {
  /** The authoritative measurement the PD comes from. */
  measurementId: string | null;
  /** On an issued prescription, the figures frozen when it was issued; on a draft, the linked measurement's figures as they are now. */
  pdType: PdType | null;
  rightPdMm: string | null;
  leftPdMm: string | null;
  totalPdMm: string | null;
  /** The linked measurement has since been voided. An issued prescription keeps its frozen figures regardless. */
  measurementVoided: boolean;
}

export interface ClinicalPrescriptionDetail {
  id: string;
  examinationId: string;
  examinationNumber: string;
  customerId: string;
  prescriptionNumber: string | null;
  status: ClinicalPrescriptionStatus;
  right: EyePrescription;
  left: EyePrescription;
  pd: ClinicalPrescriptionPd;
  /** YYYY-MM-DD, the tenant's business date of issue. */
  issuedOn: string | null;
  /** YYYY-MM-DD, only as the examiner entered it. */
  expiresOn: string | null;
  notes: string | null;
  examinerName: string | null;
  branchName: string | null;
}

export interface EyeExaminationSummary {
  id: string;
  customerId: string;
  examinationNumber: string;
  status: EyeExaminationStatus;
  displayStatus: EyeExaminationDisplayStatus;
  examinedAt: string;
  branchId: string;
  branchName: string | null;
  examinerId: string | null;
  examinerName: string | null;
  hasPrescription: boolean;
  prescriptionNumber: string | null;
  replacesExaminationId: string | null;
  replacedByExaminationId: string | null;
  createdAt: string;
  completedAt: string | null;
  voidedAt: string | null;
}

export interface EyeExaminationDetail extends EyeExaminationSummary {
  version: number;
  distanceAcuityNotation: DistanceAcuityNotation;
  nearAcuityNotation: NearAcuityNotation;
  reasonForVisit: string | null;
  clinicalNotes: string | null;
  acuities: VisualAcuityEntry[];
  refractions: RefractionEntry[];
  prescription: ClinicalPrescriptionDetail | null;
  amendmentReason: string | null;
  voidReason: string | null;
  replacesExaminationNumber: string | null;
  replacedByExaminationNumber: string | null;
  createdByName: string | null;
  updatedAt: string;
  updatedByName: string | null;
  completedByName: string | null;
  voidedByName: string | null;
}

/** Someone who may be named examiner: active, holding medical.complete, and able to work in the examination's branch. */
export interface EligibleExaminer {
  id: string;
  name: string;
}

export interface EyeInput {
  sphere?: string;
  cylinder?: string;
  axis?: number;
  add?: string;
  prism?: string;
  prismBase?: PrismBase;
}

/** The whole draft. Saving replaces it: what is left out is cleared, not kept. */
export interface SaveEyeExaminationInput {
  examinedAt?: string;
  examinerId?: string | null;
  distanceAcuityNotation?: DistanceAcuityNotation;
  nearAcuityNotation?: NearAcuityNotation;
  reasonForVisit?: string | null;
  clinicalNotes?: string | null;
  acuities?: {
    distance: AcuityDistance;
    correction: AcuityCorrection;
    right?: string;
    left?: string;
    binocular?: string;
  }[];
  refractions?: { kind: RefractionKind; right?: EyeInput; left?: EyeInput }[];
  /** Null or left out: no prescription on this draft. */
  prescription?: {
    right?: EyeInput;
    left?: EyeInput;
    pdMeasurementId?: string | null;
    expiresOn?: string | null;
    notes?: string | null;
  } | null;
}

export interface UpdateEyeExaminationInput extends SaveEyeExaminationInput {
  /** The version the screen was showing. A stale one is refused with 409. */
  version: number;
}

/** The reason length the API accepts for an amendment, a void or a discard, after trimming. */
export const CLINICAL_REASON_LIMITS = { min: 3, max: 500 } as const;

/** Starting an amendment or voiding a completed examination: why. Kept on the record, never copied into the audit log. */
export interface ClinicalReasonInput {
  reason: string;
}

/** Discarding a draft: why, and the version the screen was showing. A stale one is refused with 409. */
export interface DiscardEyeExaminationInput extends ClinicalReasonInput {
  version: number;
}
